import { useCallback, useEffect, useState } from "react";
import { organizationUnitApi } from "../services/organizationUnitApi.js";
import { permissionApi } from "../services/permissionApi.js";
import { roleApi } from "../services/roleApi.js";
import { userApi } from "../services/userApi.js";
import type { CmtsAdminAssignmentOption } from "../types/adminIdentity.types.js";
import type { EntityRecord } from "../types/api.types.js";
import { normalizeUserSafeApiError } from "../core/api/apiErrorNormalizer.js";

function readString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}
function idOf(row: EntityRecord): string { return readString(row.id) ?? readString(row.roleId) ?? readString(row.permissionId) ?? readString(row.orgUnitId) ?? readString(row.code) ?? ""; }
function labelOf(row: EntityRecord): string { return readString(row.label) ?? readString(row.name) ?? readString(row.displayName) ?? readString(row.code) ?? idOf(row); }
function toOption(row: EntityRecord): CmtsAdminAssignmentOption { return { id: idOf(row), code: readString(row.code), label: labelOf(row), disabled: row.active === false || row.status === "Inactive" }; }

export function useCmtsAssignmentMatrix(kind: "user-role" | "role-permission" | "user-org-scope", ownerId?: string | undefined): {
  readonly options: readonly CmtsAdminAssignmentOption[];
  readonly loading: boolean;
  readonly error?: string | undefined;
  readonly refresh: () => Promise<void>;
  readonly assign: (optionId: string) => Promise<void>;
  readonly remove: (optionId: string) => Promise<void>;
} {
  const [options, setOptions] = useState<readonly CmtsAdminAssignmentOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(undefined);
    try {
      const result = kind === "role-permission" ? await permissionApi.list({ page: 1, pageSize: 250, activeOnly: true }) : kind === "user-org-scope" ? await organizationUnitApi.list({ page: 1, pageSize: 250, activeOnly: true }) : await roleApi.list({ page: 1, pageSize: 250, activeOnly: true });
      setOptions(result.items.map(toOption).filter((option) => option.id));
    } catch (err) {
      setOptions([]);
      setError(normalizeUserSafeApiError(err).userMessage || "Assignment options could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [kind]);

  useEffect(() => { void refresh(); }, [refresh]);

  const assign = useCallback(async (optionId: string) => {
    if (!ownerId) throw new Error("Select a record before assigning.");
    if (kind === "user-role") await userApi.assignRole?.(ownerId, { roleId: optionId });
    if (kind === "user-org-scope") await userApi.assignOrganizationScope?.(ownerId, { scopeId: optionId });
    if (kind === "role-permission") await roleApi.assignPermission?.(ownerId, { permissionId: optionId });
    await refresh();
  }, [kind, ownerId, refresh]);

  const remove = useCallback(async (optionId: string) => {
    if (!ownerId) throw new Error("Select a record before removing assignment.");
    if (kind === "user-role") await userApi.removeRole?.(ownerId, optionId);
    if (kind === "user-org-scope") await userApi.removeOrganizationScope?.(ownerId, optionId);
    if (kind === "role-permission") await roleApi.removePermission?.(ownerId, optionId);
    await refresh();
  }, [kind, ownerId, refresh]);

  return { options, loading, error, refresh, assign, remove };
}
